import React, { useRef, useState, KeyboardEvent, useEffect } from 'react';
import { Wrapper, Header, IconWrapper, Icon, TextContainer, MainText, SubText, SaveButton, CancelButton, Body, BodyText, ButtonsWrapper, Footer, FooterText, FooterIcon, AIButton, AIButtonIcon, AIButtonText, StyledInputAreaAI } from './styled';

import InputAreaAI from "components/InputAreaAI";
import { DataType } from "components/InputAreaAI/components/Assistant/data";
import { useAssistant } from "components/InputAreaAI";

// @ts-ignore
import rewardsIcon from "assets/images/journeys/purpose/rewards.svg";

// @ts-ignore
import consequencesIcon from "assets/images/journeys/purpose/consequences.svg";

// @ts-ignore
import obstaclesIcon from "assets/images/journeys/purpose/obstacles.svg";

// @ts-ignore
import solutionsIcon from "assets/images/journeys/purpose/solutions.svg";

// @ts-ignore
import editIcon from "assets/images/journeys/purpose/edit.svg";

// @ts-ignore
import aiIcon from "assets/images/journeys/purpose/ai.svg";

export enum ItemType {
  Rewards = 'rewards',
  Consequences = 'consequences',
  Obstacles = 'obstacles',
  Solutions = 'solutions'
}

interface ItemProps {
  type: ItemType;
  text?: string;
  onChange?: (type: ItemType, value: string, callback?: () => void) => void;
  area?: string;       // Area name from parent component
  color?: string;      // Area color from parent component
  category?: string;   // Category name from parent component
  goal_name?: string;  // Goal name from parent component
  goal_id?: string;    // Goal ID from parent component
}

const Item: React.FC<ItemProps> = ({ type, text, onChange, area, color, category, goal_name, goal_id }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(text || '');
  const [isSaving, setIsSaving] = useState(false);
  const [isAIButtonEnabled, setIsAIButtonEnabled] = useState(false);

  const $inputRef = useRef(null);
  const { currentOpen, setCurrentOpen } = useAssistant();
  const inputId = `input-${type}-${goal_id}`;
  const isAIOpen = currentOpen === inputId;
  
  // Reset editedText and isEditing state when text prop changes or when switching to a different goal
  useEffect(() => {
    setEditedText(text || '');
    setIsEditing(false);
    setIsSaving(false);
    setIsAIButtonEnabled(false);
  }, [text, goal_id]);

  const getTypeLabel = () => {
    switch (type) {
      case ItemType.Rewards:
        return "Rewards";
      case ItemType.Consequences:
        return "Consequences";
      case ItemType.Obstacles:
        return "Obstacles";
      case ItemType.Solutions:
        return "Solutions";
      default:
        return "If I do";
    }
  };

  const getMainText = () => {
    switch (type) {
      case ItemType.Rewards:
        return "If I Do";
      case ItemType.Consequences:
        return "If I Don't";
      case ItemType.Obstacles:
        return "What's Blocking Me";
      case ItemType.Solutions:
        return "How I'll Do It";
      default:
        return "If I do";
    }
  };

  const getSubText = () => {
    switch (type) {
      case ItemType.Rewards:
        return "What will I gain if I follow through on this goal?";
      case ItemType.Consequences:
        return "What will happen if I don't achieve this goal?";
      case ItemType.Obstacles:
        return "What internal or external challenges might hold me back?";
      case ItemType.Solutions:
        return "How will I overcome those obstacles and take action?";
      default:
        return "Rewards";
    }
  };

  const getPlaceholderText = () => {
    switch (type) {
      case ItemType.Rewards:
        return "I’ll be able to stay more engaged and productive during work hours, make clearer decisions, and have more energy left for personal activities and relationships.";
      case ItemType.Consequences:
        return "I may struggle with fatigue, distractions, and lower motivation, which can reduce both my work performance and overall satisfaction.";
      case ItemType.Obstacles:
        return "Unstructured routines, poor sleep, multitasking, and constant digital interruptions can make it difficult to maintain focus and energy levels consistently.";
      case ItemType.Solutions:
        return "I’ll establish a structured daily routine that includes focused work blocks, short breaks, proper rest, and limited screen distractions. I’ll also build small recovery habits like hydration, movement, and brief mindfulness sessions.";
      default:
        return "Rewards";
    }
  };

  const getIconSrc = () => {
    switch (type) {
      case ItemType.Rewards:
        return rewardsIcon;
      case ItemType.Consequences:
        return consequencesIcon;
      case ItemType.Obstacles:
        return obstaclesIcon;
      case ItemType.Solutions:
        return solutionsIcon;
      default:
        return rewardsIcon;
    }
  };

  const handleEditClick = () => {
    setIsEditing(true);

    setTimeout(() => {
      // @ts-ignore
      $inputRef.current?.focus();
    }, 50);

    // Enable AI button after 500ms delay to prevent accidental clicks
    setTimeout(() => {
      setIsAIButtonEnabled(true);
    }, 500);
  };

  const handleCancelClick = () => {
    setIsEditing(false);
    setEditedText(text || '');
    setIsAIButtonEnabled(false);
  };

  const handleSaveClick = () => {
    setIsSaving(true);

    if (onChange) {
      onChange(type, editedText.trim(), () => {
        // Only hide the textarea after the API call is successful
        setIsEditing(false);
        setIsSaving(false);
        setEditedText(editedText.trim());
        setIsAIButtonEnabled(false);
      });
    }
  };

  const handleInputChange = (value: string) => {
    setEditedText(value);
  };

  const handleInputKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveClick();
    }
  };

  const handleAIButtonClick = () => {
    if (!isAIButtonEnabled) return;
    
    if (isAIOpen) {
      setCurrentOpen(null);
    } else {
      setTimeout(() => {
        setCurrentOpen(inputId);
      }, 150);
    }
  };

  return (
    <Wrapper type={type} isEditing={isEditing}>
      <Header>
        <TextContainer>
          <MainText type={type}>{getMainText()}</MainText>
          <SubText>{getSubText()}</SubText>
        </TextContainer>
        <IconWrapper>
          <Icon src={getIconSrc()} alt={`${type} icon`} />
        </IconWrapper>
      </Header>

      <Body isEditing={isEditing}>
        {isEditing ? (
          <StyledInputAreaAI>
            <InputAreaAI
              id={inputId}
              value={editedText}
              name={type}
              style={{ fontSize: 16, wordBreak: 'break-word' }}
              max={255}
              ref={$inputRef}
              onChange={handleInputChange}
              onKeyDown={handleInputKeyDown}
              disabled={isSaving}
              hideSuggestButton={true}
              assistant={{
                type: type === ItemType.Rewards ? DataType.REWARD :
                      type === ItemType.Consequences ? DataType.CONSEQUENCE :
                      type === ItemType.Obstacles ? DataType.OBSTACLES :
                      type === ItemType.Solutions ? DataType.SOLUTIONS : DataType.ACTION,
                color: color || '##ccc',
                title: getTypeLabel(),
                order: "1",
                area: area || String(type),
                category: category || getTypeLabel(),
                goal: goal_name,
                prompt: editedText
              }}
            />
          </StyledInputAreaAI>
        ) : (
          <BodyText onClick={handleEditClick} isExample={!text} hasValue={!!text}>
            {text || getPlaceholderText()}
          </BodyText>
        )}
      </Body>

      {isEditing ? (
        <Footer>
          <ButtonsWrapper>
            <SaveButton
              onClick={handleSaveClick}
              disabled={editedText.trim() === (text || '') || isSaving}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </SaveButton>

            <CancelButton
              onClick={handleCancelClick}
              disabled={isSaving}
            >
              Cancel
            </CancelButton>
          </ButtonsWrapper>

          <AIButton
            onClick={handleAIButtonClick}
            disabled={isSaving || !isAIButtonEnabled}
          >
            <AIButtonIcon src={aiIcon} alt="AI" />
            <AIButtonText>AI</AIButtonText>
          </AIButton>
        </Footer>
      ) : (
        <Footer isExample>
          <FooterText onClick={handleEditClick}>
            {text ? "Click here to edit" : "Click to customize this example"}
          </FooterText>
          <FooterIcon
            src={editIcon}
            alt="Edit"
            onClick={handleEditClick}
          />
        </Footer>
      )}

    </Wrapper>
  );
}

export default Item;
